import React from "react";
import { AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Extract active health flags from a snapshot dict. Returns null when nothing
 * needs surfacing so callers can render conditionally.
 */
function flagsFromSnapshot(snap) {
  if (!snap) return null;
  const items = [];
  if (snap.allergies_answer === "yes")
    items.push({ label: "Allergies", detail: (snap.allergies_text || "").trim() });
  if (snap.medications_answer === "yes")
    items.push({ label: "Medications", detail: (snap.medications_text || "").trim() });
  if (snap.pregnancy_answer === "yes")
    items.push({ label: "Pregnancy", detail: "" });
  return items.length ? items : null;
}

/**
 * Amber ⚠ icon with hover-tooltip listing the flagged health conditions of one
 * or more guests attached to a booking. Renders nothing when there is nothing
 * to surface.
 *
 * Props:
 *  - guests: [{ name?, snapshot }] — one entry per guest on the booking.
 *  - size:   "xs" | "sm" | "md"
 *  - testId: optional data-testid override
 */
export default function HealthWarning({ guests, size = "sm", testId }) {
  const list = (guests || [])
    .filter(Boolean)
    .map((g) => ({ name: g.name, items: flagsFromSnapshot(g.snapshot) }))
    .filter((g) => g.items && g.items.length);
  if (!list.length) return null;

  const iconClass =
    size === "xs" ? "h-3 w-3" : size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center text-amber-500 dark:text-amber-400 cursor-help shrink-0"
            data-testid={testId || "health-warning"}
            aria-label="Health advisory"
            onClick={(e) => e.stopPropagation()}
          >
            <AlertTriangle className={iconClass} strokeWidth={2.5} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-amber-950 text-amber-50 border border-amber-500/40">
          <div className="space-y-2 text-xs">
            {list.map((g, i) => (
              <div key={i}>
                {g.name && list.length > 1 && (
                  <div className="font-medium mb-0.5 text-amber-200">{g.name}</div>
                )}
                <ul className="space-y-0.5">
                  {g.items.map((it, j) => (
                    <li key={j}>
                      <span className="font-medium">{it.label}:</span>{" "}
                      {it.detail || "Yes"}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
